export const APPLY_RESULT = {
    ERROR: 'error',
    FLOW: 'flow',
    INCOMPLETE: 'incomplete',
    FAILED: 'failed',
};

function asArray(value) {
    return Array.isArray(value) ? value : [];
}

export function mapApplyChannelsToSelect(payloadGroups, getChannelGroupKey) {
    return asArray(payloadGroups)
        .map((group) => {
            const channelDef = group && group.channelDef;
            if (!channelDef) {
                return null;
            }
            const groupKey = (typeof getChannelGroupKey === 'function' ? getChannelGroupKey(channelDef) : null)
                || channelDef.realId
                || channelDef.id;
            return {
                groupKey,
                channelDef,
                channels: asArray(group.channels),
            };
        })
        .filter((group) => group && group.channels.length > 0);
}

export function classifyApplyScenarioResult(payload) {
    if (!payload) {
        return {type: APPLY_RESULT.FAILED};
    }

    if (payload.errors) {
        return {
            type: APPLY_RESULT.ERROR,
            errors: payload.errors,
        };
    }

    const flows = asArray(payload.flows);
    if (flows.length > 0) {
        return {
            type: APPLY_RESULT.FLOW,
            flow: flows[0],
            resultLinks: asArray(payload.resultLinks),
        };
    }

    const channelsToSelect = asArray(payload.channelsToSelect);
    const channelsToFix = asArray(payload.channelsToFix);
    const channelsWithConfidentialMode = asArray(payload.channelsWithConfidentialMode);
    const channelDefsToAdd = asArray(payload.channelDefsToAdd);

    if (
        channelsToSelect.length > 0 ||
        channelsToFix.length > 0 ||
        channelsWithConfidentialMode.length > 0 ||
        channelDefsToAdd.length > 0
    ) {
        return {
            type: APPLY_RESULT.INCOMPLETE,
            channelsToSelect,
            channelsToFix,
            channelsWithConfidentialMode,
            channelDefsToAdd,
        };
    }

    return {type: APPLY_RESULT.FAILED};
}

export function shouldShowAppliedFlowDescription(flow) {
    return Boolean(
        flow &&
        (flow.appliedDescription || (flow.flowDef && flow.flowDef.appliedDescription))
    );
}

export function shouldShowFinishAppliedDescription(descriptionFlow, hasBankIntroGuide) {
    return Boolean(descriptionFlow) && !hasBankIntroGuide;
}

export function resolveAppliedDescriptionFlow(organisationFlow, stateFlow) {
    if (shouldShowAppliedFlowDescription(organisationFlow)) {
        return organisationFlow;
    }
    if (shouldShowAppliedFlowDescription(stateFlow)) {
        return stateFlow;
    }
    return null;
}

export function resolveApplyResultFlow(organisationFlow, stateFlow) {
    if (organisationFlow && organisationFlow.realId) {
        return organisationFlow;
    }
    if (stateFlow && stateFlow.realId) {
        return stateFlow;
    }
    return null;
}

export function applySuccessFetchVariables(flow, preconfiguredFlowId) {
    const flowId = (flow && flow.realId) || preconfiguredFlowId || null;
    return flowId ? {flowId} : null;
}

export function shouldRenderEmbeddedFlowConfig(relayState) {
    return Boolean(relayState && relayState.props);
}

export function resolvePostConnectWizardStep(capturedNextStep, currentStep) {
    const captured = Number(capturedNextStep);
    const current = Number(currentStep);
    if (!Number.isFinite(captured)) {
        return Number.isFinite(current) ? current : 0;
    }
    if (!Number.isFinite(current)) {
        return captured;
    }
    return Math.max(captured, current);
}

function channelIdentityIds(channel) {
    if (!channel) {
        return [];
    }
    return [channel.realId, channel.id].filter((value) => value != null && value !== '').map(String);
}

function groupChannelIds(group) {
    return (group && group.channels ? group.channels : []).reduce((acc, channel) => {
        return acc.concat(channelIdentityIds(channel));
    }, []);
}

export function isChooserGroupSelected(group, channelSelect) {
    if (!group || !channelSelect) {
        return false;
    }
    const groupKey = group.groupKey;
    if (groupKey && channelSelect[groupKey]) {
        return true;
    }
    const groupIds = {};
    groupChannelIds(group).forEach((id) => {
        groupIds[id] = true;
    });
    if (Object.keys(groupIds).length === 0) {
        return false;
    }
    return Object.keys(channelSelect).some((key) => {
        return channelIdentityIds(channelSelect[key]).some((id) => groupIds[id]);
    });
}

export function hasAllChooserSelections(channelsToSelect, channelSelect) {
    return (channelsToSelect || []).every((group) => isChooserGroupSelected(group, channelSelect));
}

export function shouldRewindIncompleteApply(nextSelect, channelSelect) {
    return (nextSelect || []).some((group) => !isChooserGroupSelected(group, channelSelect));
}

export function pinChannelSelectToChooserGroups(channelSelect, groups) {
    const next = Object.assign({}, channelSelect || {});
    (groups || []).forEach((group) => {
        if (!group || !group.groupKey || next[group.groupKey]) {
            return;
        }
        const groupIds = {};
        groupChannelIds(group).forEach((id) => {
            groupIds[id] = true;
        });
        const match = Object.keys(next)
            .map((key) => next[key])
            .find((channel) => channelIdentityIds(channel).some((id) => groupIds[id]));
        if (match) {
            next[group.groupKey] = match;
        }
    });
    return next;
}

export function getFirstUnselectedChooserStep({
    prefixSteps = 0,
    alreadyConnectedCount = 0,
    channelsToConnectCount = 0,
    channelsToSelect = [],
    channelSelect = {},
} = {}) {
    const start = Number(prefixSteps) + Number(alreadyConnectedCount) + Number(channelsToConnectCount);
    const groups = channelsToSelect || [];
    for (let i = 0; i < groups.length; i += 1) {
        if (!isChooserGroupSelected(groups[i], channelSelect)) {
            return start + i;
        }
    }
    return null;
}

export function resolveMissingChooserRewind({
    alreadyRewound,
    currentStep,
    prefixSteps = 0,
    alreadyConnectedCount = 0,
    channelsToConnectCount = 0,
    channelsToSelect = [],
    channelSelect = {},
} = {}) {
    const targetStep = getFirstUnselectedChooserStep({
        prefixSteps,
        alreadyConnectedCount,
        channelsToConnectCount,
        channelsToSelect,
        channelSelect,
    });
    if (alreadyRewound || targetStep == null || Number(targetStep) === Number(currentStep)) {
        return {shouldRewind: false, targetStep};
    }
    return {shouldRewind: true, targetStep};
}

export function shouldShowCouldNotCreateFlow({applyFailed, errors, flow} = {}) {
    return Boolean(applyFailed && !errors && !flow);
}
